import * as React from "react"
import cx from "classnames"

import * as styles from "./styles.module.scss"
import { FlowInputCallback, FlowQuestionId } from "types"
import InputOption from "../InputOption"

export interface InputPickManyProps {
  answers: any[]
  flowKey: FlowQuestionId
  maxAnswers?: number
  minAnswers?: number
  nextCallback?: Function
  onSubmit: FlowInputCallback
  userAnswer: any
}

const getMinMax = (answers: any[], minAnswers: number, maxAnswers: number) => {
  // default min and max
  const min = minAnswers || 1
  const max = maxAnswers || answers.length
  // make sure config is sane
  if (min > answers.length || min > max) {
    throw new Error(
      "Error in flow config. minAnswers and/or maxAnswers in pick-many input type is invalid."
    )
  }
  return { min, max }
}

const getHelpText = (
  answers: any[],
  minAnswers: number,
  maxAnswers: number
) => {
  const { min, max } = getMinMax(answers, minAnswers, maxAnswers)
  if (min === max) {
    return `Choose ${min} options.`
  }
  return `Choose between ${min} and ${max} options.`
}

const getIsDisabled = (
  answers: any[],
  minAnswers: number,
  maxAnswers: number,
  selected: any
) => {
  const { max } = getMinMax(answers, minAnswers, maxAnswers)
  return selected?.length >= max
}

const getIsValid = (
  answers: any[],
  minAnswers: number,
  maxAnswers: number,
  selected: any
) => {
  const { min, max } = getMinMax(answers, minAnswers, maxAnswers)
  return selected.length >= min && selected.length <= max
}

const getIsSelected = (selected: any, id: string) => {
  return selected.some((a) => a.id === id)
}

const getAnswerValue = (selected: any, id: string) => {
  const sel = selected.find((a) => a.id === id)
  return (sel && sel.value) || null
}

const InputPickMany: React.FC<InputPickManyProps> = ({
  answers,
  flowKey,
  maxAnswers,
  minAnswers = 1,
  nextCallback,
  onSubmit,
  userAnswer,
}) => {
  const helpText = getHelpText(answers, minAnswers, maxAnswers)
  const [selected, setSelected] = React.useState(value)
  const [isDisabled, setIsDisabled] = React.useState<boolean>(
    getIsDisabled(answers, minAnswers, maxAnswers, value)
  )
  const { value = [] } = userAnswer
  const [isValid, setIsValid] = React.useState<boolean>(
    getIsValid(answers, minAnswers, maxAnswers, selected, value)
  )

  // note: shouldSelect is provided by caller rather than computed here
  // because our "other" input on Option has custom logic about when to clear itself
  // it's not simply a function of clicking to toggle
  const onSelect = (id, shouldSelect, value) => {
    const isSelected = getIsSelected(selected, id)
    // check if select is allowed
    if (!isSelected && isDisabled) {
      return
    }
    // filter out value optimistically because:
    //  - we might be deselecting (shouldSelect === false)
    //  - we want to guard against concatting duplicate (shouldSelect === true)
    let newSelected = selected.filter((a) => a.id !== id)
    if (shouldSelect) {
      newSelected = newSelected.concat([{ id, value }])
    }

    setSelected(newSelected)
    setIsDisabled(getIsDisabled(answers, minAnswers, maxAnswers, newSelected))
    setIsValid(getIsValid(answers, minAnswers, maxAnswers, newSelected))
  }

  const onSubmitFormat = () => {
    if (!isValid || !onSubmit) {
      return
    }
    // concatenate titles of selected answers
    const display = answers
      .filter((a) => getIsSelected(selected, a.id))
      .map((a) => a.title)
      .join(", ")
    onSubmit({
      flowKey,
      value: selected,
      display,
    })
  }

  return (
    <div className={styles.pickOne}>
      <div
        className={cx({
          [styles.container]: true,
          [styles.containerList]: true,
        })}
      >
        <p className={styles.instructions}>{helpText}</p>
        <ul
          className={cx({
            [styles.answers]: true,
            [styles.answersList]: true,
          })}
        >
          {answers.map((answer) => {
            const isSelected = getIsSelected(selected, answer.id)
            return (
              <InputOption
                key={`${flowKey}-${answer.id}`}
                isSelected={isSelected}
                isDisabled={isDisabled}
                flowKey={flowKey}
                answer={answer}
                onSelect={(shouldSelect = !isSelected, value = answer.title) =>
                  onSelect(answer.id, shouldSelect, value)
                }
                value={getAnswerValue(selected, answer.id)}
              />
            )
          })}
        </ul>
        <a
          className={cx({
            [styles.next]: true,
            [styles.disabled]: !isValid,
          })}
          onClick={() => onSubmitFormat()}
          data-flow-next-callback={nextCallback}
          data-qa="submit"
        >
          <i className="bsk-icon-arrow-stick-right-thick"></i>
        </a>
      </div>
    </div>
  )
}

export default InputPickMany
